import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import qrcode from "qrcode-generator";
import { X } from "@phosphor-icons/react";
import { api } from "../api";
import { useDialog } from "../useDialog";
import { GRADES, gradeLabel } from "../../shared/grades";
import { courseForGrade, type CourseId } from "../../shared/math/course-catalog";
import { courseLabel } from "../math/course-presentation";
import { defaultDailyXpGoalFor } from "../../shared/engagement/config";
import type { GettingStarted, ParentStudent } from "../types";

// First run for a family (docs/plans/ONBOARDING.md, phase 2): add the children on one screen, then hand
// the app to one of them, on this device or their own. Everything else has a default and lives in
// settings for later.

const suggestPin = () => String(1000 + (crypto.getRandomValues(new Uint16Array(1))[0]! % 9000));
interface Row { key: number; name: string; grade: number | ""; pin: string }
const blankRow = (key: number): Row => ({ key, name: "", grade: "", pin: suggestPin() });

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useDialog<HTMLElement>(onClose);
  return <div className="modal-backdrop parent-club-overlay" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section ref={ref} tabIndex={-1} className="modal parent-club-dialog" role="dialog" aria-modal="true" aria-label={title}><header><div><span className="eyebrow">Getting started</span><h2>{title}</h2></div>
      <button type="button" className="icon-button" aria-label={`Close ${title}`} onClick={onClose}><X /></button></header>{children}</section></div>;
}

export function FamilySetup({ onDone }: { onDone: () => Promise<void> }) {
  const [rows, setRows] = useState<Row[]>([blankRow(1)]);
  const [added, setAdded] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const update = (key: number, patch: Partial<Row>) => setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const ready = rows.filter((row) => row.name.trim());
    if (!ready.length) { setError("Add at least one child."); return; }
    if (ready.some((row) => row.grade === "")) { setError("Choose a grade for each child."); return; }
    if (ready.some((row) => !/^\d{4}$/.test(row.pin))) { setError("Each PIN is 4 digits."); return; }
    setBusy(true); setError("");
    const saved: Array<{ id: string; name: string }> = [];
    try {
      for (const row of ready) {
        const { id } = await api<{ id: string }>("/api/parent/students", { method: "POST", body: JSON.stringify({
          name: row.name.trim(), pin: row.pin, grade: row.grade, unitType: "xp", dailyTarget: defaultDailyXpGoalFor(row.grade === "" ? null : row.grade), lessonRate: 0, offWeekdays: [], offDays: [],
        }) });
        saved.push({ id, name: row.name.trim() });
      }
      setAdded(saved);
    } catch (caught) {
      // Children saved before the error stay saved; show them and let the parent fix the rest.
      setRows(rows.filter((row) => !saved.some((child) => child.name === row.name.trim())));
      if (saved.length) setAdded(saved);
      setError(caught instanceof Error ? caught.message : "Could not save");
    } finally { setBusy(false); }
  }

  if (added.length && !error) return <HandOver children={added} onLater={onDone} />;
  return <section className="family-setup">
    <span className="eyebrow">Step 1 of 2</span>
    <h1>Who’s learning?</h1>
    <p className="muted">Add each child to your family. We’ll use their grade and a short check to find a starting point.</p>
    {added.length > 0 && <p className="setup-saved">Saved: {added.map((child) => child.name).join(", ")}.</p>}
    <form onSubmit={save}>
      {rows.map((row, index) => <fieldset className="setup-child" key={row.key}>
        <legend>{index === 0 ? "Child" : `Child ${index + 1}`}</legend>
        <label>First name<input value={row.name} onChange={(event) => update(row.key, { name: event.target.value })} maxLength={40} autoComplete="off" required={index === 0} autoFocus={index === 0} /></label>
        <label>Grade<select value={row.grade} onChange={(event) => update(row.key, { grade: event.target.value === "" ? "" : Number(event.target.value) })} required={Boolean(row.name.trim())}>
          <option value="" disabled>Choose a grade</option>{GRADES.map((grade) => <option key={grade.value} value={grade.value}>{grade.label}</option>)}</select></label>
        <label>PIN for signing in<input className="setup-pin" value={row.pin} onChange={(event) => update(row.key, { pin: event.target.value.replace(/\D/g, "").slice(0, 4) })} inputMode="numeric" pattern="[0-9]{4}" maxLength={4} aria-describedby={`pin-note-${row.key}`} /></label>
        <small id={`pin-note-${row.key}`}>Tell {row.name.trim() || "your child"} this PIN. You can change it.</small>
        {row.grade !== "" && <small className="setup-course">Starts in {courseLabel(courseForGrade(row.grade))}</small>}
        {rows.length > 1 && <button type="button" className="text-link" onClick={() => setRows(rows.filter((other) => other.key !== row.key))}>Remove</button>}
      </fieldset>)}
      <button type="button" className="button ghost" onClick={() => setRows([...rows, blankRow(Math.max(...rows.map((row) => row.key)) + 1)])}>+ Add another child</button>
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="button primary wide" disabled={busy}>{busy ? "Saving…" : "Continue"}</button>
      {added.length > 0 && <button type="button" className="text-link" onClick={() => setError("")}>Skip the rest</button>}
    </form>
  </section>;
}

function HandOver({ children, onLater }: { children: Array<{ id: string; name: string }>; onLater: () => Promise<void> }) {
  const [device, setDevice] = useState<{ id: string; name: string } | null>(null);
  const first = children[0]!;
  return <section className="family-setup">
    <span className="eyebrow">Step 2 of 2</span>
    <h1>Ready for {children.length === 1 ? first.name : "the first session"}?</h1>
    <p className="muted">The first session starts with a short, friendly check to find where {children.length === 1 ? first.name : "each child"} should begin.</p>
    <div className="handover-options">
      {children.map((child) => <article className="handover-child" key={child.id}><strong>{child.name}</strong>
        <StartHereButton student={child} primary={child.id === first.id} />
        <button className="button ghost" onClick={() => setDevice(child)}>Set up {child.name}’s own device</button></article>)}
    </div>
    <button className="text-link" onClick={() => void onLater()}>Later: go to the family dashboard</button>
    {device && <DeviceLinkDialog student={device} onClose={() => setDevice(null)} />}
  </section>;
}

// Signs this tab in as the child. The parent signs back in with their own password afterwards.
export function StartHereButton({ student, primary = false }: { student: { id: string; name: string }; primary?: boolean }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  async function start() {
    if (!window.confirm(`Hand this device to ${student.name}? You’ll sign back in with your password afterwards.`)) return;
    setBusy(true); setError("");
    try { await api(`/api/parent/students/${student.id}/handoff`, { method: "POST" }); window.location.assign("/"); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not switch"); setBusy(false); }
  }
  return <>{<button className={primary ? "button primary" : "button"} disabled={busy} onClick={() => void start()}>{busy ? "Switching…" : `Start ${student.name}’s session on this device`}</button>}
    {error && <p className="form-error" role="alert">{error}</p>}</>;
}

function qrSvg(text: string) {
  const qr = qrcode(0, "M"); qr.addData(text); qr.make();
  const size = qr.getModuleCount(), cells: string[] = [];
  for (let row = 0; row < size; row++) for (let col = 0; col < size; col++) if (qr.isDark(row, col)) cells.push(`M${col + 4},${row + 4}h1v1h-1z`);
  return { size: size + 8, path: cells.join("") };
}

export function DeviceLinkDialog({ student, onClose }: { student: { id: string; name: string }; onClose: () => void }) {
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null), [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api<{ url: string; expiresAt: string }>("/api/parent/devices", { method: "POST", body: JSON.stringify({ studentId: student.id }) })
      .then((made) => { if (live) setLink(made); }).catch((caught) => { if (live) setError(caught instanceof Error ? caught.message : "Could not make a link"); });
    return () => { live = false; };
  }, [student.id]);
  const code = link ? qrSvg(link.url) : null;
  return <Dialog title={`Set up ${student.name}’s device`} onClose={onClose}>
    {!link && !error && <p>Making a link…</p>}
    {error && <p className="form-error" role="alert">{error}</p>}
    {link && code && <div className="device-link">
      <p>On {student.name}’s tablet or computer, scan this code with the camera, or open the link. It opens {student.name}’s sign-in, and the device remembers your family.</p>
      <svg className="device-qr" viewBox={`0 0 ${code.size} ${code.size}`} role="img" aria-label={`QR code for ${student.name}’s setup link`} shapeRendering="crispEdges"><rect width="100%" height="100%" fill="#fff" /><path d={code.path} fill="#000" /></svg>
      <input className="device-url" readOnly value={link.url} onFocus={(event) => event.currentTarget.select()} aria-label="Setup link" />
      <small>Works once, for the next 15 minutes. You can make a new one any time.</small>
    </div>}
  </Dialog>;
}

const LEVEL_TEXT = { ahead: ", ahead of grade level", at: ", right at grade level", building: ", building foundations first" } as const;
const STAGE_TEXT: Record<GettingStarted["stage"], (name: string, course: string, placed: number, level?: GettingStarted["level"]) => string> = {
  added: (name, course) => `Ready for ${name}’s first session in ${course}.`,
  checking: (name) => `${name} is partway through the starting-point check.`,
  placed: (name, course, placed, level) => `${name} is starting in ${course}${level ? LEVEL_TEXT[level] : ""}${placed ? `, with ${placed} topics already known` : ""}. Next: the first lesson.`,
  learning: (name, course) => `${name} is learning in ${course}.`,
};

// Shown on the parent overview until every child has finished a first lesson.
export function GettingStartedCard({ students }: { students: ParentStudent[] }) {
  const [device, setDevice] = useState<{ id: string; name: string } | null>(null);
  const starting = students.filter((student) => student.active && student.gettingStarted && student.gettingStarted.stage !== "learning");
  if (!starting.length) return null;
  return <article className="getting-started"><h3>Getting started</h3>
    {starting.map((student) => { const start = student.gettingStarted!; return <div className="getting-started-row" key={student.id}>
      <p><strong>{student.name}</strong>{student.grade != null && <small> · {gradeLabel(student.grade)}</small>}<br />{STAGE_TEXT[start.stage](student.name, courseLabel(start.course as CourseId), start.placedTopics, start.level)}</p>
      <div className="getting-started-actions"><StartHereButton student={student} primary={start.stage === "added"} /><button className="button ghost" onClick={() => setDevice(student)}>Set up {student.name}’s device</button></div>
    </div>; })}
    {device && <DeviceLinkDialog student={device} onClose={() => setDevice(null)} />}
  </article>;
}
